import React, { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import "./mcTabNav.css";
import { getVisibleMcTabs, useCurrentMcProfile } from "../utils/profileAccess";

export default function McTabNav({ className = "", activeKey = "" }) {
  const navRef = useRef(null);
  const profile = useCurrentMcProfile();
  const tabs = getVisibleMcTabs(profile);
  const location = useLocation();
  const currentPath = String(location.pathname || "").replace(/\/$/, "");
  const currentHash = String(location.hash || "");

  useEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const active = nav.querySelector(".mc-tab-nav__link.is-active, a.is-active");
    if (!active) return;

    // Keep the active tab horizontally visible without ever calling
    // scrollIntoView/window.scrollTo. The Sessions page receives live updates;
    // browser-level scrolling here caused the page to jump back upward while
    // cards refreshed. Only the nav strip is allowed to scroll.
    let frame;
    const keepActiveVisible = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        try {
          const left = active.offsetLeft - (nav.clientWidth / 2) + (active.clientWidth / 2);
          nav.scrollLeft = Math.max(0, left);
        } catch (_) {}
      });
    };
    keepActiveVisible();

    // A viewport resize can change the visible portion of the strip without a
    // route change. Ignore height/content updates so live count refreshes never
    // reset a tab position the operator chose manually.
    let lastWidth = nav.clientWidth;
    const observer = typeof ResizeObserver === "function" ? new ResizeObserver(() => {
      const width = nav.clientWidth;
      if (width === lastWidth) return;
      lastWidth = width;
      keepActiveVisible();
    }) : null;
    observer?.observe(nav);

    return () => {
      observer?.disconnect();
      window.cancelAnimationFrame(frame);
    };
  }, [currentPath, currentHash, activeKey, tabs.length]);

  const normalizeTo = (value = "") => {
    const [pathPart, hashPart = ""] = String(value || "").split("#");
    return { path: pathPart.replace(/\/$/, ""), hash: hashPart ? `#${hashPart}` : "" };
  };

  return (
    <nav ref={navRef} className={["mc-tab-nav", className].filter(Boolean).join(" ")} aria-label="Morpheus Count tabs">
      {tabs.map((tab) => {
        const target = normalizeTo(tab.to);
        const isHashTab = !!target.hash;
        const pathMatches = currentPath === target.path;
        const hashMatches = currentHash === target.hash;
        const isActive = tab.key === activeKey || (isHashTab ? (pathMatches && hashMatches) : (pathMatches && !currentHash));

        return (
          <Link
            key={tab.key}
            to={tab.to}
            aria-current={isActive ? "page" : undefined}
            className={["mc-tab-nav__link", isActive ? "is-active" : ""].filter(Boolean).join(" ")}
            onClick={(event) => {
              if (isActive) {
                event.preventDefault();
                return;
              }

              // Same routing style as the stable tabs: let React Router's Link
              // own the navigation, and only signal Sessions to stop its heavy
              // live-sync work before the route changes.
              try {
                window.dispatchEvent(new CustomEvent("mc-tab-navigate-start", { detail: { to: tab.to } }));
              } catch (_) {}
            }}
          >
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}
